import {Button} from "primereact/button";
import React from "react";
import {useTranslation} from "react-i18next";

import {selectReloadLoading, selectRevaId} from "../../redux/persistenceSlice";
import {reloadPersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {selectValueOrderEntitlementsState} from "../../redux/valueOrderEntitlementsSlice";
import {loadValueOrderEntitlementsThunk} from "../../redux/valueOrderEntitlementsThunk";

/**
 * Reload button for the order tab. Refreshes the currently loaded valuation with
 * `forceUpdateExternalData=true`, bypassing the ~1h cache for external resources
 * (Value-Orders / inspection protocols).
 *
 * Delegates to {@link reloadPersistence}: updates the store on success and, on failure, only records
 * a reload error without invalidating the loaded valuation. If already loaded, it also re-fetches the order
 * entitlements of the user's ValueAG client (reva-rest still answers from its own cache).
 */
export default function OrderReloadButton(): React.ReactNode {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();
    const revaId = useRevaSelector(selectRevaId);
    const loading = useRevaSelector(selectReloadLoading);
    const entitlementsLoaded = useRevaSelector(selectValueOrderEntitlementsState).loadedAt !== null;

    if (!revaId) {
        return null;
    }

    return (
        <Button
            icon="pi pi-refresh"
            text
            // Square sizing + spacing come from the `.InfoCardHeaderActions` panel-header style.
            loading={loading}
            aria-label={t("order.revaOrder.reload")}
            tooltip={t("order.revaOrder.reloadTooltip")}
            tooltipOptions={{position: "left"}}
            onClick={() => {
                void dispatch(reloadPersistence({forceUpdateExternalData: true}));
                if (entitlementsLoaded) void dispatch(loadValueOrderEntitlementsThunk(true));
            }}
        />
    );
}
